import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Button, Icon, Spinner, Switch } from "@cntrl/ui";
import type {
  CommunicationPreferenceChange,
  CommunicationPreferences as Prefs,
} from "../../../app/hooks/useCommunicationPreferences.js";
import { CategoryInfo } from "./CategoryInfo.js";
import styles from "./CommunicationPreferences.module.scss";

/**
 * What each row is CALLED, what its (i) lists, and — for the rows that email — what the unsubscribe confirm lists
 * (the emails alone: Connections emails friend and roster requests, not "new friend"). The server serves the keys and
 * each channel's state (`CommunicationCategories` decides which notifications a row covers); the words are the
 * client's. A key this build does not know renders nothing rather than itself.
 */
const CATEGORY_COPY: Record<string, { label: string; items: string[]; emailItems?: string[] }> = {
  opportunities: {
    label: "Opportunities",
    items: ["Show opportunities", "Tour opportunities"],
    emailItems: ["Show opportunities", "Tour opportunities"],
  },
  band_activity: {
    label: "Band activity invitations",
    items: ["Rehearsal invites", "Content invites", "PR invites", "Placement invites"],
    emailItems: ["Rehearsal invites", "Content invites", "PR invites", "Placement invites"],
  },
  opportunity_updates: {
    label: "RSVPs & updates",
    items: ["Replies to your invites", "Backed out of a show", "Everyone's in", "Cancelled or removed"],
  },
  shows: {
    label: "Shows & travel",
    items: ["Show confirmed", "Logistics added", "Support slot invites", "Guest list", "Load-in & showtime", "Flight changes", "Show settled", "Payments closed out"],
  },
  live_activity: { label: "Day of show Live Activity", items: ["Lock screen & Dynamic Island card"] },
  connections: {
    label: "Connections",
    items: ["Friend requests", "New friends", "Roster requests & replies", "Band & team invites", "Removed from a team"],
    emailItems: ["Friend requests", "Roster requests"],
  },
  messages: { label: "Messages", items: ["Direct messages", "Chat messages", "Reactions", "Added to a show chat"] },
  tasks: { label: "Tasks", items: ["Task assigned", "Task updates"] },
  socials: { label: "Socials", items: ["Trending posts"] },
};

type Channel = "email" | "push";
const switchKey = (key: string, channel: Channel) => `${channel}:${key}`;

export interface CommunicationPreferencesProps {
  data: Prefs | undefined;
  isLoading: boolean;
  /** The read failed — for the token door, a bad or missing link. */
  isError: boolean;
  onSave: (changes: CommunicationPreferenceChange[]) => Promise<unknown>;
  saving: boolean;
  /** An email's "unsubscribe" link names its category: open on that category's confirm step. */
  unsubscribe?: string;
  /** What to say when the read failed. */
  errorMessage?: string;
}

/**
 * Communication preferences (ART-285) — ONE component behind both doors: Settings › Account opens it in a modal, and
 * a notification email's footer opens it on its own page through a token. Each door supplies the data and the save;
 * this owns the three steps — the Email · Push table, the "are you sure" an unsubscribe link lands on, and the saved
 * check.
 *
 * Unsubscribing is a PRESS, never a load: mail scanners open links before people do, and a page that turned email off
 * on arrival would unsubscribe whoever's company scans their mail.
 */
export function CommunicationPreferences({
  data, isLoading, isError, onSave, saving, unsubscribe, errorMessage,
}: CommunicationPreferencesProps) {
  const confirmable = unsubscribe && CATEGORY_COPY[unsubscribe]?.emailItems ? unsubscribe : null;
  const [step, setStep] = useState<"edit" | "confirm" | "saved">(confirmable ? "confirm" : "edit");
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const next: Record<string, boolean> = {};
    for (const c of data.categories ?? []) {
      if (!c.key) continue;
      if (c.email != null) next[switchKey(c.key, "email")] = c.email;
      if (c.push != null) next[switchKey(c.key, "push")] = c.push;
    }
    setEnabled(next);
  }, [data]);

  if (isLoading) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={20} label="Loading communication preferences…" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <p className={styles.message}>
        {errorMessage ?? "Couldn't load your communication preferences. Please try again."}
      </p>
    );
  }

  const rows = (data.categories ?? []).filter((c) => c.key && CATEGORY_COPY[c.key]);

  const save = async (changes: CommunicationPreferenceChange[]) => {
    setError(null);
    try {
      await onSave(changes);
      setStep("saved");
    } catch {
      setError("Couldn't save your communication preferences. Please try again.");
    }
  };

  if (step === "saved") {
    return (
      <div className={styles.saved} role="status">
        <span className={styles.check}>
          <Icon icon={CheckCircle2} size={32} />
        </span>
        <p className={styles.savedText}>Your communication preferences have been saved</p>
      </div>
    );
  }

  if (step === "confirm" && confirmable) {
    const copy = CATEGORY_COPY[confirmable]!;
    return (
      <div className={styles.stack}>
        <p className={styles.question}>Are you sure you want to stop receiving these emails?</p>
        <div className={styles.category}>
          <span className={styles.label}>{copy.label}</span>
          <ul className={styles.items}>
            {copy.emailItems!.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <span className={styles.desc}>Sent to {data.email}</span>
        </div>
        {error && <p className={styles.error} role="alert">{error}</p>}
        <div className={styles.actions}>
          <Button variant="ghost" type="button" onClick={() => setStep("edit")}>Keep receiving them</Button>
          <Button type="button" tone="danger" loading={saving}
            onClick={() => void save([{ key: confirmable, channel: "email", enabled: false }])}>
            Unsubscribe
          </Button>
        </div>
      </div>
    );
  }

  const cell = (key: string, label: string, channel: Channel, applies: boolean) => {
    if (!applies) return <span className={styles.none} aria-label={`No ${channel}`}>—</span>;
    const k = switchKey(key, channel);
    return (
      <Switch
        checked={enabled[k] ?? true}
        onChange={(next) => setEnabled((prev) => ({ ...prev, [k]: next }))}
        label={`${label} ${channel}`}
      />
    );
  };

  const changes = (): CommunicationPreferenceChange[] =>
    Object.entries(enabled).map(([k, value]) => {
      const [channel, key] = k.split(":");
      return { key: key!, channel: channel!, enabled: value };
    });

  return (
    <div className={styles.stack}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col"><span className={styles.srOnly}>Notification</span></th>
            <th scope="col" className={styles.channel}>Email</th>
            <th scope="col" className={styles.channel}>Push</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const { label, items } = CATEGORY_COPY[c.key!]!;
            return (
              <tr key={c.key}>
                <th scope="row" className={styles.label}>
                  <span className={styles.rowTitle}>
                    {label}
                    <CategoryInfo label={label} items={items} />
                  </span>
                </th>
                <td className={styles.channel}>{cell(c.key!, label, "email", c.email != null)}</td>
                <td className={styles.channel}>{cell(c.key!, label, "push", c.push != null)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {error && <p className={styles.error} role="alert">{error}</p>}
      <div className={styles.footer}>
        <p className={styles.desc}>Important account notifications cannot be turned off.</p>
        <Button type="button" loading={saving} onClick={() => void save(changes())}>Save</Button>
      </div>
    </div>
  );
}
